import { useCallback, useEffect, useRef, useState } from "react";

import {
  PageContainer,
  ProgressiveProfilingCompleted,
  ProgressiveProfilingStepCreatorsCompleted,
  ProgressiveProfilingStepDesignStorageCompleted,
  ProgressiveProfilingStepIndustryCompleted,
  ProgressiveProfilingStepMainCadCompleted,
  ProgressiveProfilingStepRoleCompleted,
  ProgressiveProfilingStepViewersCompleted,
  Select,
} from "@shapr3d/frontend-lib";

import { useTracker } from "../../hooks/useTracker";
import useTranslation from "../../hooks/useTranslation";
import Heading from "../Heading/Heading";
import MultistepForm from "../MultistepForm/MultistepForm";
import ProgressiveProfilingError from "../ProgressiveProfilingError/ProgressiveProfilingError";
import ProgressiveProfilingSuccess from "../ProgressiveProfilingSuccess/ProgressiveProfilingSuccess";

import style from "./ProgressiveProfiling.module.css";

type Industry = ProgressiveProfilingStepIndustryCompleted["eventProperties"]["industry"];
type NumberOf = "0" | "1-2" | "3-10" | "10+" | "Not sure";
type DesignStorage =
  ProgressiveProfilingStepDesignStorageCompleted["eventProperties"]["design_storage"];
type CadTool = ProgressiveProfilingStepMainCadCompleted["eventProperties"]["cad_tool"];
type Role = ProgressiveProfilingStepRoleCompleted["eventProperties"]["role"];

const predefinedRandomOrder = Array.from(new Array(15)).map(() => Math.random());
const mustSkipIndustryTypes: Industry[] = ["architecture", "media_and_entertainment", "education"];

const ProgressiveProfiling = () => {
  const { t, m } = useTranslation();
  const tracker = useTracker();

  const [currentStep, setCurrentStep] = useState(0);
  const [submitState, setSubmitState] = useState<"success" | "error" | "initial">("initial");
  const questionRefs = useRef(new Map<number, HTMLSelectElement>());

  const goToNext = useCallback(() => {
    // Some timeout is needed to the select closing animation to finish, greatly reduces the number of flashes
    setTimeout(() => {
      setCurrentStep(currentStep + 1);
    }, 100);
  }, [currentStep]);

  const goToPrevous = () => {
    setCurrentStep(currentStep - 1);
  };

  const submitCallback = () => {
    tracker.trackProgressiveProfilingEvent(new ProgressiveProfilingCompleted());
    setSubmitState("success");
  };

  const retryCallback = () => {
    setSubmitState("initial");
  };

  useEffect(() => {
    const tabNavigation = (event: KeyboardEvent) => {
      if (event.key === "Tab") {
        event.preventDefault();
        if (questionRefs.current.get(currentStep)?.value) {
          goToNext();
        } else {
          questionRefs.current.get(currentStep)?.focus();
        }
      }
    };

    document.addEventListener("keydown", tabNavigation);
    return () => {
      document.removeEventListener("keydown", tabNavigation);
    };
  }, [currentStep, goToNext]);

  return (
    <PageContainer>
      <div className={style.container}>
        {submitState === "initial" && (
          <>
            <Heading type="h1">{t(m.ProgressiveProfiling.General.HelpUsImproveShapr3D)}</Heading>
            <MultistepForm
              submitCallback={submitCallback}
              currentStep={currentStep}
              goToNextCallback={goToNext}
              goToPreviousCallback={goToPrevous}
              previousDisabled={false}
              nextDisabled={!questionRefs.current.get(currentStep)?.value}
            >
              <MultistepForm.Step>
                <span className={style.label}>
                  {t(m.ProgressiveProfiling.QuestionOne.WhichIndustryDoYouWorkIn)}
                </span>
                <Select
                  defaultValue=""
                  ref={(node) => {
                    node ? questionRefs.current.set(0, node) : questionRefs.current.delete(0);
                  }}
                  onChange={(event) => {
                    const value = event.currentTarget.value as Industry;
                    tracker.trackProgressiveProfilingEvent(
                      new ProgressiveProfilingStepIndustryCompleted({ industry: value })
                    );

                    const skip = mustSkipIndustryTypes.includes(value);
                    skip ? submitCallback() : goToNext();
                  }}
                >
                  <option value="" disabled hidden>
                    {t(m.ProgressiveProfiling.General.PleaseSelect)}
                  </option>
                  {(
                    [
                      {
                        message: m.ProgressiveProfiling.QuestionOne.Architecture,
                        option: "architecture",
                      },
                      {
                        message: m.ProgressiveProfiling.QuestionOne.Construction,
                        option: "construction",
                      },
                      {
                        message: m.ProgressiveProfiling.QuestionOne.ConsumerElectronics,
                        option: "consumer_electronics",
                      },
                      {
                        message: m.ProgressiveProfiling.QuestionOne.EducationStudentTeacher,
                        option: "education",
                      },
                      {
                        message: m.ProgressiveProfiling.QuestionOne.FurnitureAndInteriorDesign,
                        option: "furniture_and_interior_design",
                      },
                      {
                        message: m.ProgressiveProfiling.QuestionOne.HomeAndLifestyle,
                        option: "home_and_lifestyle",
                      },
                      {
                        message: m.ProgressiveProfiling.QuestionOne.IndustrialEquipment,
                        option: "industrial_equipment",
                      },
                      {
                        message: m.ProgressiveProfiling.QuestionOne.LifeSciencesAndHealthcare,
                        option: "life_sciences_and_healthcare",
                      },
                      {
                        message: m.ProgressiveProfiling.QuestionOne.MediaAndEntertainment,
                        option: "media_and_entertainment",
                      },
                      {
                        message:
                          m.ProgressiveProfiling.QuestionOne
                            .TransportationAutomotiveAerospaceMarine,
                        option: "transportation",
                      },
                    ] satisfies {
                      message: {
                        defaultMessage: string;
                        id: string;
                      };
                      option: Industry;
                    }[]
                  )
                    .map((value, i) => ({ value, sort: predefinedRandomOrder[i] }))
                    .sort((a, b) => a.sort - b.sort)
                    .map(({ value }, i) => (
                      <option key={i} value={value.option}>
                        {t(value.message)}
                      </option>
                    ))}
                </Select>
              </MultistepForm.Step>

              <MultistepForm.Step>
                <span className={style.label}>
                  {t(
                    m.ProgressiveProfiling.QuestionTwo
                      .HowManyPeopleCreateAndModify3DDesignsInYourCompany
                  )}
                </span>
                <Select
                  defaultValue=""
                  ref={(node) => {
                    node ? questionRefs.current.set(1, node) : questionRefs.current.delete(1);
                  }}
                  onChange={(event) => {
                    const value = event.currentTarget.value;
                    tracker.trackProgressiveProfilingEvent(
                      new ProgressiveProfilingStepCreatorsCompleted({
                        nrOfCreators: value as NumberOf,
                      })
                    );

                    goToNext();
                  }}
                >
                  <option value="" disabled hidden>
                    {t(m.ProgressiveProfiling.General.PleaseSelect)}
                  </option>
                  <option value={"0" satisfies NumberOf}>{"0"}</option>
                  <option value={"1-2" satisfies NumberOf}>{"1-2"}</option>
                  <option value={"3-10" satisfies NumberOf}>{"3-10"}</option>
                  <option value={"10+" satisfies NumberOf}>{"10+"}</option>
                  <option value={"Not sure" satisfies NumberOf}>
                    {t(m.ProgressiveProfiling.General.NotSure)}
                  </option>
                </Select>
              </MultistepForm.Step>

              <MultistepForm.Step>
                <span className={style.label}>
                  {t(
                    m.ProgressiveProfiling.QuestionThree.HowManyPeopleOnlyView3DDesignsInYourCompany
                  )}
                </span>
                <Select
                  defaultValue=""
                  ref={(node) => {
                    node ? questionRefs.current.set(2, node) : questionRefs.current.delete(2);
                  }}
                  onChange={(event) => {
                    const value = event.currentTarget.value;
                    tracker.trackProgressiveProfilingEvent(
                      new ProgressiveProfilingStepViewersCompleted({
                        nrOfViewers: value as NumberOf,
                      })
                    );

                    goToNext();
                  }}
                >
                  <option value="" disabled hidden>
                    {t(m.ProgressiveProfiling.General.PleaseSelect)}
                  </option>
                  <option value={"0" satisfies NumberOf}>{"0"}</option>
                  <option value={"1-2" satisfies NumberOf}>{"1-2"}</option>
                  <option value={"3-10" satisfies NumberOf}>{"3-10"}</option>
                  <option value={"10+" satisfies NumberOf}>{"10+"}</option>
                  <option value={"Not sure" satisfies NumberOf}>
                    {t(m.ProgressiveProfiling.General.NotSure)}
                  </option>
                </Select>
              </MultistepForm.Step>

              <MultistepForm.Step>
                <span className={style.label}>
                  {t(
                    m.ProgressiveProfiling.QuestionFour
                      .WhatMainMethodDoesYourCompanyUseToStoreDesigns
                  )}
                </span>
                <Select
                  defaultValue=""
                  ref={(node) => {
                    node ? questionRefs.current.set(3, node) : questionRefs.current.delete(3);
                  }}
                  onChange={(event) => {
                    const value = event.currentTarget.value;
                    tracker.trackProgressiveProfilingEvent(
                      new ProgressiveProfilingStepDesignStorageCompleted({
                        designStorage: value as DesignStorage,
                      })
                    );

                    goToNext();
                  }}
                >
                  <option value="" disabled hidden>
                    {t(m.ProgressiveProfiling.General.PleaseSelect)}
                  </option>
                  <option value={"no_method" satisfies DesignStorage}>
                    {t(m.ProgressiveProfiling.QuestionFour.NoSpecificMethod)}
                  </option>
                  <option value={"general_cloud_services" satisfies DesignStorage}>
                    {t(m.ProgressiveProfiling.QuestionFour.GeneralCloudServices)}
                  </option>
                  <option value={"own_servers" satisfies DesignStorage}>
                    {t(m.ProgressiveProfiling.QuestionFour.OwnServers)}
                  </option>
                  <option value={"pdm_plm" satisfies DesignStorage}>
                    {t(m.ProgressiveProfiling.QuestionFour.PdmPlmSystems)}
                  </option>
                  <option value={"other" satisfies DesignStorage}>
                    {t(m.ProgressiveProfiling.General.Other)}
                  </option>
                </Select>
              </MultistepForm.Step>

              <MultistepForm.Step>
                <span className={style.label}>
                  {t(
                    m.ProgressiveProfiling.QuestionFive.WhatIsTheMain2D3DDesignToolUsedAtYourCompany
                  )}
                </span>
                <Select
                  defaultValue=""
                  ref={(node) => {
                    node ? questionRefs.current.set(4, node) : questionRefs.current.delete(4);
                  }}
                  onChange={(event) => {
                    const value = event.currentTarget.value;
                    tracker.trackProgressiveProfilingEvent(
                      new ProgressiveProfilingStepMainCadCompleted({ cadTool: value as CadTool })
                    );

                    goToNext();
                  }}
                >
                  <option value="" disabled hidden>
                    {t(m.ProgressiveProfiling.General.PleaseSelect)}
                  </option>
                  <option value={"alias" satisfies CadTool}>{"Alias"}</option>
                  <option value={"autocad" satisfies CadTool}>{"AutoCAD"}</option>
                  <option value={"catia" satisfies CadTool}>{"Catia"}</option>
                  <option value={"creo" satisfies CadTool}>{"CREO"}</option>
                  <option value={"fusion_360" satisfies CadTool}>{"Fusion 360"}</option>
                  <option value={"inventor" satisfies CadTool}>{"Inventor"}</option>
                  <option value={"onshape" satisfies CadTool}>{"Onshape"}</option>
                  <option value={"rhino" satisfies CadTool}>{"Rhino"}</option>
                  <option value={"shapr3d" satisfies CadTool}>{"Shapr3D"}</option>
                  <option value={"siemens_nx" satisfies CadTool}>{"Siemens NX"}</option>
                  <option value={"sketchup" satisfies CadTool}>{"SketchUp"}</option>
                  <option value={"solid_edge" satisfies CadTool}>{"Solid Edge"}</option>
                  <option value={"solidworks" satisfies CadTool}>{"SOLIDWORKS"}</option>
                  <option value={"none" satisfies CadTool}>
                    {t(m.ProgressiveProfiling.General.None)}
                  </option>
                  <option value={"other" satisfies CadTool}>
                    {t(m.ProgressiveProfiling.General.Other)}
                  </option>
                </Select>
              </MultistepForm.Step>

              <MultistepForm.Step>
                <span className={style.label}>
                  {t(
                    m.ProgressiveProfiling.QuestionSix
                      .WhichOfTheseRolesDescribesYouTheBestWithinTheCompany
                  )}
                </span>
                <Select
                  defaultValue=""
                  ref={(node) => {
                    node ? questionRefs.current.set(5, node) : questionRefs.current.delete(5);
                  }}
                  onChange={async (event) => {
                    const value = event.currentTarget.value;
                    tracker.trackProgressiveProfilingEvent(
                      new ProgressiveProfilingStepRoleCompleted({ role: value as Role })
                    );

                    submitCallback();
                  }}
                >
                  <option value="" disabled hidden>
                    {t(m.ProgressiveProfiling.General.PleaseSelect)}
                  </option>
                  {(
                    [
                      {
                        message: m.ProgressiveProfiling.QuestionSix.CEOLeadershipRole,
                        option: "ceo_leadership",
                      },
                      {
                        message: m.ProgressiveProfiling.QuestionSix.EngineerResearchAndDevelopment,
                        option: "engineer_research_and_development",
                      },
                      {
                        message: m.ProgressiveProfiling.QuestionSix.EngineerToolDesign,
                        option: "engineer_tool_design",
                      },
                      {
                        message: m.ProgressiveProfiling.QuestionSix.EntrepreneurOwner,
                        option: "entrepreneur_owner",
                      },
                      {
                        message: m.ProgressiveProfiling.QuestionSix.IndustrialDesigner,
                        option: "industrial_designer",
                      },
                      {
                        message: m.ProgressiveProfiling.QuestionSix.ProductManager,
                        option: "product_manager",
                      },
                      {
                        message: m.ProgressiveProfiling.QuestionSix.QualityEngineer,
                        option: "quality_engineer",
                      },
                      {
                        message: m.ProgressiveProfiling.QuestionSix.SalesPurchasing,
                        option: "sales_purchasing",
                      },
                      {
                        message: m.ProgressiveProfiling.QuestionSix.TeamLeadEngineerDesign,
                        option: "team_lead",
                      },
                      {
                        message: m.ProgressiveProfiling.QuestionSix.TechnicianMachinistOperator,
                        option: "technician_machinist_operator",
                      },
                      {
                        message: m.ProgressiveProfiling.QuestionSix.TestEngineer,
                        option: "test_engineer",
                      },
                    ] satisfies {
                      message: {
                        defaultMessage: string;
                        id: string;
                      };
                      option: Role;
                    }[]
                  )
                    .map((value, i) => ({ value, sort: predefinedRandomOrder[i] }))
                    .sort((a, b) => a.sort - b.sort)
                    .map(({ value }, i) => (
                      <option key={i} value={value.option}>
                        {t(value.message)}
                      </option>
                    ))}
                </Select>
              </MultistepForm.Step>
            </MultistepForm>
          </>
        )}

        {submitState === "success" && <ProgressiveProfilingSuccess />}
        {submitState === "error" && <ProgressiveProfilingError retryCallback={retryCallback} />}
      </div>
    </PageContainer>
  );
};

export default ProgressiveProfiling;
